/* eslint-disable react/jsx-props-no-spreading */
import { useUrlGenerator } from '@folklore/routes';
import { useTracking } from '@folklore/tracking';
import classNames from 'classnames';
import { differenceInSeconds, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useLocation } from 'wouter';

import { useAnswerQuestion } from '../../hooks/useGame';
import * as AppPropTypes from '../../lib/PropTypes';
import { getComponentFromName, getImageAtSize } from '../../lib/utils';

import { useThemeClassName } from '../../contexts/ThemeContext';
import * as FeedbackComponents from '../animations/feedbacks';
import QuestionsIndicator from '../lists/QuestionsIndicator';
import Question from '../questions/Question';
import GameEnd from './GameEnd';

import styles from '../../../styles/games/wheel.module.scss';

const propTypes = {
    className: PropTypes.string,
    game: AppPropTypes.game,
    participation: AppPropTypes.participation,
    reload: PropTypes.func,
};

const defaultProps = {
    className: null,
    game: null,
    participation: null,
    reload: null,
};

function WheelGame({ className, game, participation, reload }) {
    const themeClassName = useThemeClassName(styles);
    const route = useUrlGenerator();
    const [, navigate] = useLocation();
    const tracking = useTracking();
    const {
        id: gameId,
        title = null,
        subtitle = null,
        description = null,
        headerImage = null,
        questions = null,
        theme: gameTheme = null,
        blocks = null,
    } = game || {};

    const endBlocks = useMemo(
        () => (blocks || []).filter(({ placement }) => placement === 'end'),
        [blocks],
    );

    const { feedback: feedbackComponent = null, feedbackValid, feedbackInvalid } = gameTheme || {};
    const FeedbackComponent = getComponentFromName(FeedbackComponents, feedbackComponent, 'Thumbs');
    const {
        id: participationId = null,
        endedAt: participationEndedAt = null,
        answers: userAnswers = [],
    } = participation || {};
    const participationEnded =
        participationEndedAt !== null &&
        differenceInSeconds(parseISO(participationEndedAt), new Date()) <= 0;

    const mediumImage = getImageAtSize(headerImage, 'medium');
    const { url: mediumImageUrl = null } = mediumImage || {};

    const wantedQuestionIndex = useMemo(() => {
        const unansweredQuestions = questions.filter(
            ({ id }) =>
                userAnswers.findIndex(
                    ({ question, valid = null }) => question === id && valid !== null,
                ) === -1,
        );

        if (unansweredQuestions.length > 0) {
            const { id: unansweredId } = unansweredQuestions[0];
            return questions.findIndex(({ id }) => id === unansweredId);
        }

        if (unansweredQuestions.length === 0) {
            return questions.length - 1;
        }

        return 0;
    }, [userAnswers, questions]);

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(wantedQuestionIndex);
    const [ended, setEnded] = useState(participationEnded);
    const [feedback, setFeedback] = useState(null);

    const currentQuestion = questions !== null ? questions[currentQuestionIndex] || null : null;
    const { id: questionId } = currentQuestion || {};

    const currentAnswer =
        (userAnswers || []).find(({ question }) => question === questionId) || null;

    useEffect(() => {
        let timeout = null;
        if (participationEnded) {
            timeout = setTimeout(() => {
                setEnded(participationEnded);
            }, 4000);
        }
        if (participationId === null) {
            setCurrentQuestionIndex(wantedQuestionIndex);
        }
        return () => {
            if (timeout !== null) {
                clearTimeout(timeout);
                timeout = null;
            }
        };
    }, [participationId]);

    const { answerQuestionAsync, isMutating: loading } = useAnswerQuestion({
        game,
        participation,
    });

    const answerQuestion = useCallback(() => {
        answerQuestionAsync({ questionId, answer: null }).then(
            ({
                participation: updatedParticipation,
                participant: updatedParticipant,
                game: updatedGame = null,
            }) => {
                const { answers = [], ended: newEnded = false } = updatedParticipation || {};
                const answer = answers.find(({ question }) => question === questionId);
                const { valid = null, validated = false, value } = answer;

                tracking.trackInteraction('game_answer_question', {
                    game: updatedGame,
                    participant: updatedParticipant,
                    participation: updatedParticipation,
                    question: currentQuestion,
                    answer: value,
                    valid,
                    validated,
                });

                if (newEnded) {
                    tracking.trackInteraction('game_ended', {
                        game: updatedGame,
                        participant: updatedParticipant,
                        participation: updatedParticipation,
                    });
                }
            },
        );
    }, [gameId, questionId, setFeedback]);

    const onAnimationEnd = useCallback(() => {
        setFeedback(true);
    }, [setFeedback]);

    const gotoNextQuestion = useCallback(() => {
        setFeedback(null);
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
            navigate(route('participation', { participation: participationId }), { replace: true });
        }
    }, [participationId, questions, currentQuestionIndex]);

    const onFeedbackComplete = useCallback(() => {
        gotoNextQuestion();
        if (participationEnded) {
            setEnded(true);
        }
    }, [gotoNextQuestion, participationEnded]);

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                {
                    [styles.ended]: ended === true,
                },
                className,
            ])}
        >
            <div className={styles.header}>
                <div className={styles.title}>
                    {mediumImageUrl !== null ? (
                        <img src={mediumImageUrl} alt={title} className={styles.image} />
                    ) : (
                        <h1>{title}</h1>
                    )}
                </div>
                {subtitle !== null ? <h2 className={styles.subtitle}>{subtitle}</h2> : null}
                {description !== null && !ended ? (
                    <div className={styles.description}>{description}</div>
                ) : null}
            </div>
            <TransitionGroup className={styles.gameContainer}>
                {!ended ? (
                    <CSSTransition key="game" classNames={styles} timeout={200}>
                        <div className={styles.game}>
                            <TransitionGroup className={styles.questions}>
                                {currentQuestion !== null ? (
                                    <CSSTransition
                                        key={`question-${questionId}`}
                                        classNames={styles}
                                        timeout={200}
                                    >
                                        <div className={styles.question}>
                                            <div className={styles.inner}>
                                                <Question
                                                    question={currentQuestion}
                                                    answer={currentAnswer}
                                                    loading={loading}
                                                    onAnswer={answerQuestion}
                                                    onAnimationEnd={onAnimationEnd}
                                                    className={styles.choices}
                                                />
                                            </div>
                                        </div>
                                    </CSSTransition>
                                ) : null}
                            </TransitionGroup>
                            {questions.length > 1 ? (
                                <QuestionsIndicator
                                    currentIndex={currentQuestionIndex}
                                    questions={questions}
                                    answers={userAnswers}
                                    currentIconComponent="sparkle"
                                    iconComponent="thumb"
                                    className={styles.navigation}
                                    withProgress
                                />
                            ) : null}
                        </div>
                    </CSSTransition>
                ) : (
                    <CSSTransition key="end" classNames={styles} timeout={200}>
                        <GameEnd
                            game={game}
                            reload={reload}
                            participation={participation}
                            blocks={endBlocks}
                            onClickReplay={reload}
                            className={styles.end}
                        />
                    </CSSTransition>
                )}
            </TransitionGroup>

            {FeedbackComponent !== null ? (
                <FeedbackComponent
                    className={classNames([
                        styles.feedback,
                        {
                            [styles.isActive]: feedback !== null,
                        },
                    ])}
                    valid={feedback}
                    validLabel={feedbackValid}
                    invalidLabel={feedbackInvalid}
                    onAnimationComplete={onFeedbackComplete}
                />
            ) : null}
        </div>
    );
}

WheelGame.propTypes = propTypes;
WheelGame.defaultProps = defaultProps;

export default WheelGame;
